import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useBonusDetails } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { EmptyState, Icon, Spinner, Toast } from '@/ui/common';
import { BonusDetailsPanel } from '@/ui/profile';

export const Route = createFileRoute('/profile/bonuses/$bonusType/$bonusId')({
  component: ProfileBonusDetailsPage,
});

function ProfileBonusDetailsPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const navigate = useNavigate();
  const { bonusType: bonusTypeParam, bonusId } = Route.useParams();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const bonusType = Number.parseInt(bonusTypeParam, 10);
  const typeValid = Number.isFinite(bonusType);
  const { details, isLoading, error, currency } = useBonusDetails(
    typeValid ? bonusType : null,
    bonusId || null,
    typeValid && Boolean(bonusId),
  );

  const onCta = (kind: 'continueBetting' | 'useFreeBet' | 'viewTicket', ticketId?: string) => {
    if (kind === 'continueBetting') {
      void navigate({ to: '/sports' });
      return;
    }
    if (kind === 'useFreeBet') {
      void navigate({ to: '/betslip' });
      return;
    }
    if (kind === 'viewTicket') {
      void navigate({ to: '/profile/tickets' });
      void ticketId;
    }
  };

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <Link
        to="/profile/bonuses"
        className="inline-flex w-fit items-center gap-2 text-[13px] text-text-secondary hover:text-text-primary"
      >
        <Icon name="arrow-right" size={16} className="-scale-x-100" />
        {label('Bonuses', 'Bonuses')}
      </Link>

      <header className="flex flex-col gap-1 lg:gap-2">
        <h1 className="text-[18px] font-bold text-text-primary lg:text-[22px]">
          {label('Bonus details', 'Bonus details')}
        </h1>
      </header>

      {error ? (
        <Toast tone="danger">
          {label('Could not load bonus details', 'Could not load bonus details')}
          {error.message ? `: ${error.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading bonus', 'Loading bonus')} />
        </div>
      ) : details ? (
        <BonusDetailsPanel
          details={details}
          currency={currency}
          termsLabel={label('Terms', 'Terms')}
          bonusAmountLabel={label('Bonus amount', 'Bonus amount')}
          rewardAmountLabel={label('Reward amount', 'Reward amount')}
          wageringProgressLabel={label('Wagering progress', 'Wagering progress')}
          startDateLabel={label('Start date', 'Start date')}
          endDateLabel={label('End date', 'End date')}
          onCta={onCta}
        />
      ) : !error ? (
        <EmptyState
          icon="bonuses"
          title={label('Bonus not found', 'Bonus not found')}
          description={label(
            'This bonus could not be loaded.',
            'This bonus could not be loaded.',
          )}
        />
      ) : null}
    </div>
  );
}
